import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Typography,
  Button,
  Form,
  Input,
  Space,
  Tag,
  App as AntApp,
  Row,
  Col,
  Statistic,
  Tabs,
  Avatar,
  Tooltip,
  Divider,
  Select,
  Alert,
  Spin,
  Table,
  Empty,
  Badge,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  BankOutlined,
  ApartmentOutlined,
  TeamOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  ClusterOutlined,
  BranchesOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './OrganizationSettings.css';

const { Title, Text, Paragraph } = Typography;

const OrganizationSettings = () => {
  const { user } = useAuth();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [logoPreview, setLogoPreview] = useState('');

  // Id da organização à qual o usuário logado pertence.
  const orgId = user?.organization?.id;

  const formatCnpj = (val) => {
    if (!val) return '-';
    const clean = String(val).replace(/\D/g, '');
    if (clean.length === 14) {
      return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return val;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? '-' : d.toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const copyToClipboard = (text, label = 'Texto') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copiado para a área de transferência!`);
  };

  const load = async (showMsg = false) => {
    if (!orgId) {
      message.warning('Nenhuma organização vinculada ao usuário logado.');
      return;
    }
    try {
      setLoading(true);
      const data = await api.organizations.getById(orgId);
      setOrg(data);
      setLogoPreview(data.logoUrl || '');
      form.setFieldsValue({
        name: data.name,
        cnpj: data.cnpj || '',
        website: data.website || '',
        dominio: data.dominio || '',
        logoUrl: data.logoUrl || '',
        sswBaseUrl: data.sswBaseUrl || '',
      });
      if (showMsg) message.success('Dados da organização atualizados');
    } catch (e) {
      message.error(e.message || 'Erro ao carregar organização');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleSave = async (values) => {
    try {
      setSaving(true);
      // Envia somente campos permitidos pelo endpoint de atualização de organização
      const payload = {
        name: values.name,
        cnpj: values.cnpj || undefined,
        website: values.website || undefined,
        dominio: values.dominio || undefined,
        logoUrl: values.logoUrl || undefined,
        sswBaseUrl: values.sswBaseUrl || undefined,
      };
      await api.organizations.update(orgId, payload);
      message.success('Organização atualizada com sucesso');
      await load();
      setActiveTab('overview');
    } catch (e) {
      message.error(e.message || 'Erro ao salvar organização');
    } finally {
      setSaving(false);
    }
  };

  if (!orgId) {
    return (
      <div className="org-settings-container">
        <Card className="org-header-card">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" align="center">
                <Text strong style={{ fontSize: 16 }}>Nenhuma Organização Vinculada</Text>
                <Text type="secondary">
                  O usuário atual não possui vínculo com uma organização (Tenant).
                  Entre em contato com um administrador do sistema.
                </Text>
              </Space>
            }
          />
        </Card>
      </div>
    );
  }

  const branchesCount = org?._count?.branches ?? (org?.branches?.length || 0);
  const usersCount = org?._count?.users ?? (org?.users?.length || 0);
  const employeesCount = org?._count?.employees ?? 0;
  const veiculosCount = org?._count?.veiculos ?? 0;

  const branchColumns = [
    {
      title: 'Sigla',
      dataIndex: 'sigla',
      key: 'sigla',
      width: 100,
      render: (text) => <Tag color="blue" style={{ fontWeight: 600 }}>{text || '-'}</Tag>,
    },
    {
      title: 'Nome da Filial',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 120,
      render: (val) => val ? <Text code>{val}</Text> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      render: (active) => (
        <Badge
          status={active ? 'success' : 'default'}
          text={active ? 'Ativa' : 'Inativa'}
        />
      ),
    },
  ];

  return (
    <div className="org-settings-container">
      {/* Cabeçalho Executivo do Tenant */}
      <Card className="org-header-card" loading={loading && !org}>
        {org && (
          <div className="org-header-flex">
            <div className="org-header-profile">
              <Avatar
                size={76}
                shape="square"
                src={org.logoUrl}
                icon={<BankOutlined />}
                className="org-avatar-badge"
                style={{
                  backgroundColor: '#1677ff',
                  borderRadius: 14,
                  fontSize: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {org.name ? org.name.substring(0, 2).toUpperCase() : 'OR'}
              </Avatar>

              <div className="org-title-area">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Title level={2} className="org-name">{org.name}</Title>
                  <Tag
                    color={org.isActive ? 'green' : 'red'}
                    icon={org.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12 }}
                  >
                    {org.isActive ? 'Tenant Ativo' : 'Tenant Inativo'}
                  </Tag>
                </div>

                <div className="org-tags-row">
                  {org.dominio ? (
                    <Tag color="cyan" icon={<BranchesOutlined />}>
                      ERP SSW: <strong>{org.dominio}</strong>
                    </Tag>
                  ) : (
                    <Tag color="default">Sem domínio ERP</Tag>
                  )}

                  {org.organization_groups ? (
                    <Tag color="purple" icon={<ClusterOutlined />}>
                      Grupo: <strong>{org.organization_groups.name}</strong>
                    </Tag>
                  ) : (
                    <Tag color="default" icon={<ClusterOutlined />}>
                      Tenant Independente
                    </Tag>
                  )}

                  {org.cnpj && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      CNPJ: <Text strong>{formatCnpj(org.cnpj)}</Text>
                    </Text>
                  )}
                </div>

                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ID: <Text code style={{ fontSize: 11 }}>{org.id}</Text>
                    <Tooltip title="Copiar Tenant ID">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined style={{ fontSize: 12 }} />}
                        onClick={() => copyToClipboard(org.id, 'Tenant ID')}
                      />
                    </Tooltip>
                  </Text>
                </div>
              </div>
            </div>

            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => load(true)}
                loading={loading}
              >
                Atualizar
              </Button>
              {activeTab !== 'edit' ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setActiveTab('edit')}
                >
                  Editar Dados
                </Button>
              ) : (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setActiveTab('overview')}
                >
                  Visualizar Dados
                </Button>
              )}
            </Space>
          </div>
        )}
      </Card>

      {/* Grid de KPIs / Métricas Rápidas do Tenant */}
      {org && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="org-stat-card" bordered={false}>
              <Statistic
                title="Filiais Vinculadas"
                value={branchesCount}
                prefix={<ApartmentOutlined style={{ color: '#1677ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="org-stat-card" bordered={false}>
              <Statistic
                title="Usuários Ativos"
                value={usersCount}
                prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="org-stat-card" bordered={false}>
              <Statistic
                title="Colaboradores"
                value={employeesCount}
                prefix={<SafetyCertificateOutlined style={{ color: '#722ed1' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="org-stat-card" bordered={false}>
              <Statistic
                title="Frota de Veículos"
                value={veiculosCount}
                prefix={<CarOutlined style={{ color: '#fa8c16' }} />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Destaque do Grupo de Organizações (organization_groups) */}
      {org && (
        <>
          {org.organization_groups ? (
            <Card
              className="org-group-highlight-card"
              title={
                <Space align="center">
                  <ClusterOutlined style={{ color: '#722ed1', fontSize: 18 }} />
                  <span style={{ fontSize: 16, fontWeight: 600 }}>
                    Grupo Corporativo Vinculado
                  </span>
                  <Tag color="purple" style={{ marginLeft: 6 }}>
                    Rede / Holding
                  </Tag>
                </Space>
              }
              extra={
                <Tag color={org.organization_groups.isActive ? 'green' : 'red'}>
                  {org.organization_groups.isActive ? 'Grupo Ativo' : 'Grupo Inativo'}
                </Tag>
              }
            >
              <Descriptions
                column={{ xs: 1, sm: 2, md: 3 }}
                size="middle"
                bordered
              >
                <Descriptions.Item label="Nome do Grupo">
                  <Text strong style={{ fontSize: 15, color: '#722ed1' }}>
                    {org.organization_groups.name}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Identificador (ID)">
                  <Space>
                    <Text code>{org.organization_groups.id}</Text>
                    <Tooltip title="Copiar ID do Grupo">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(org.organization_groups.id, 'ID do Grupo')}
                      />
                    </Tooltip>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Status Operacional">
                  <Badge
                    status={org.organization_groups.isActive ? 'success' : 'error'}
                    text={org.organization_groups.isActive ? 'Ativo e Operante' : 'Inativo'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Descrição do Grupo" span={3}>
                  {org.organization_groups.description ? (
                    <Text>{org.organization_groups.description}</Text>
                  ) : (
                    <Text type="secondary" italic>Nenhuma descrição cadastrada para este grupo.</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Data de Criação do Grupo">
                  {formatDate(org.organization_groups.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Última Atualização">
                  {formatDate(org.organization_groups.updatedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Governança">
                  <Tag color="geekblue">Multi-Tenant Corporativo</Tag>
                </Descriptions.Item>
              </Descriptions>

              <Alert
                message="Compartilhamento de Governança e Regras"
                description={
                  <span>
                    A organização <strong>{org.name}</strong> está associada ao grupo empresarial{' '}
                    <strong>{org.organization_groups.name}</strong>, permitindo padronização de integrações,
                    políticas corporativas e visibilidade consolidada para administradores de rede.
                  </span>
                }
                type="info"
                showIcon
                icon={<ClusterOutlined />}
                style={{ marginTop: 16 }}
              />
            </Card>
          ) : (
            <Card className="org-empty-group-card">
              <Row align="middle" justify="space-between" gutter={[16, 16]}>
                <Col xs={24} md={18}>
                  <Space align="start" size={16}>
                    <Avatar
                      size={44}
                      icon={<ClusterOutlined />}
                      style={{ backgroundColor: 'rgba(0,0,0,0.06)', color: '#8c8c8c' }}
                    />
                    <div>
                      <Text strong style={{ fontSize: 15, display: 'block' }}>
                        Nenhum Grupo Corporativo Vinculado (Tenant Independente)
                      </Text>
                      <Text type="secondary">
                        Esta organização opera atualmente de forma autônoma e isolada. Caso faça parte de uma rede,
                        holding ou grupo de empresas, o vínculo pode ser configurado por um administrador global.
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                  <Tag color="default" icon={<ClusterOutlined />}>Modo Independente</Tag>
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}

      {/* Painel com Abas Principais */}
      <Card className="org-content-card" loading={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: (
                <Space>
                  <BankOutlined />
                  <span>Visão Geral do Tenant</span>
                </Space>
              ),
              children: org ? (
                <div>
                  <Descriptions
                    title={
                      <div className="org-section-header">
                        <BankOutlined style={{ color: '#1677ff' }} />
                        <span>Dados Cadastrais da Organização</span>
                      </div>
                    }
                    column={{ xs: 1, sm: 2, md: 3 }}
                    bordered
                    size="middle"
                  >
                    <Descriptions.Item label="Razão Social / Nome">{org.name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="CNPJ">
                      {org.cnpj ? <Text strong>{formatCnpj(org.cnpj)}</Text> : <Text type="secondary">Não informado</Text>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={org.isActive ? 'green' : 'red'}>
                        {org.isActive ? 'Ativa' : 'Inativa'}
                      </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Domínio ERP SSW">
                      {org.dominio ? (
                        <Tag color="blue" style={{ fontSize: 13, fontWeight: 600 }}>
                          {org.dominio}
                        </Tag>
                      ) : (
                        <Text type="secondary">Não configurado</Text>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Website Institucional" span={2}>
                      {org.website ? (
                        <a href={org.website} target="_blank" rel="noopener noreferrer">
                          <Space>
                            <GlobalOutlined />
                            <span>{org.website}</span>
                            <LinkOutlined style={{ fontSize: 12 }} />
                          </Space>
                        </a>
                      ) : (
                        <Text type="secondary">Não informado</Text>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="URL de Login SSW" span={3}>
                      {org.sswBaseUrl ? (
                        <Space>
                          <Text code>{org.sswBaseUrl}</Text>
                          <Tooltip title="Copiar URL">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(org.sswBaseUrl, 'URL SSW')}
                            />
                          </Tooltip>
                        </Space>
                      ) : (
                        <Space>
                          <Text code>https://sistema.ssw.inf.br/bin/ssw0422</Text>
                          <Tag color="default">Padrão Global do Ambiente</Tag>
                        </Space>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Cadastrada em">
                      {formatDate(org.createdAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Última Atualização" span={2}>
                      {formatDate(org.updatedAt)}
                    </Descriptions.Item>
                  </Descriptions>

                  <Divider style={{ margin: '28px 0 20px 0' }} />

                  {/* Seção de Filiais */}
                  <div className="org-section-header">
                    <ApartmentOutlined style={{ color: '#1677ff' }} />
                    <span>Filiais Cadastradas nesta Organização ({branchesCount})</span>
                  </div>

                  {org.branches && org.branches.length > 0 ? (
                    <Table
                      dataSource={org.branches}
                      columns={branchColumns}
                      rowKey="id"
                      pagination={org.branches.length > 5 ? { pageSize: 5 } : false}
                      size="small"
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="Nenhuma filial cadastrada"
                      description="Esta organização ainda não possui filiais registradas no sistema."
                    />
                  )}
                </div>
              ) : null,
            },

            {
              key: 'group',
              label: (
                <Space>
                  <ClusterOutlined />
                  <span>Grupo Corporativo</span>
                  {org?.organization_groups && (
                    <Badge count={1} style={{ backgroundColor: '#722ed1' }} />
                  )}
                </Space>
              ),
              children: (
                <div>
                  {org?.organization_groups ? (
                    <div>
                      <div className="org-section-header">
                        <ClusterOutlined style={{ color: '#722ed1' }} />
                        <span>Informações Estruturais do Grupo</span>
                      </div>

                      <Descriptions column={{ xs: 1, sm: 2 }} bordered size="middle">
                        <Descriptions.Item label="Nome do Grupo Corporativo">
                          <Text strong style={{ fontSize: 16, color: '#722ed1' }}>
                            {org.organization_groups.name}
                          </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Identificador Único (ID)">
                          <Space>
                            <Text code>{org.organization_groups.id}</Text>
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(org.organization_groups.id, 'ID do Grupo')}
                            />
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Situação Cadastral">
                          <Tag color={org.organization_groups.isActive ? 'green' : 'red'}>
                            {org.organization_groups.isActive ? 'Grupo Ativo' : 'Grupo Inativo'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Data de Criação do Grupo">
                          {formatDate(org.organization_groups.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Descrição Corporativa" span={2}>
                          {org.organization_groups.description || 'Nenhuma descrição detalhada informada.'}
                        </Descriptions.Item>
                      </Descriptions>

                      <div style={{ marginTop: 24 }}>
                        <Card
                          size="small"
                          style={{ background: 'rgba(114, 46, 209, 0.03)', borderColor: 'rgba(114, 46, 209, 0.2)' }}
                        >
                          <Space align="start">
                            <InfoCircleOutlined style={{ color: '#722ed1', fontSize: 16, marginTop: 4 }} />
                            <div>
                              <Text strong style={{ display: 'block', color: '#722ed1' }}>
                                Gestão Centralizada de Multi-Organizações
                              </Text>
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                Organizações vinculadas ao mesmo grupo podem compartilhar catálogos de etiquetas,
                                unificar integrações e permitir que administradores corporativos transitem entre as
                                empresas do grupo com controle de permissões centralizado.
                              </Text>
                            </div>
                          </Space>
                        </Card>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '30px 0', textAlign: 'center' }}>
                      <ClusterOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
                      <Title level={4} style={{ margin: 0 }}>Nenhum Grupo Vinculado</Title>
                      <Paragraph type="secondary" style={{ maxWidth: 500, margin: '12px auto' }}>
                        Esta organização não está vinculada a nenhuma rede ou grupo empresarial.
                        Para vincular a um grupo corporativo existente, solicite ao Super Administrador do sistema.
                      </Paragraph>
                    </div>
                  )}
                </div>
              ),
            },

            {
              key: 'edit',
              label: (
                <Space>
                  <EditOutlined />
                  <span>Editar Organização</span>
                </Space>
              ),
              children: (
                <div>
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    initialValues={{
                      sswBaseUrl: 'https://sistema.ssw.inf.br/bin/ssw0422',
                    }}
                  >
                    <Row gutter={24}>
                      {/* Dados Básicos */}
                      <Col xs={24} md={12}>
                        <div className="org-section-header">
                          <BankOutlined style={{ color: '#1677ff' }} />
                          <span>Identificação da Empresa</span>
                        </div>

                        <Form.Item
                          name="name"
                          label="Razão Social / Nome da Organização"
                          rules={[{ required: true, message: 'Nome da organização é obrigatório' }]}
                        >
                          <Input placeholder="Ex: Diálogo Logística Inteligente" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="cnpj"
                          label="CNPJ"
                          tooltip="Formato com 14 dígitos (numérico ou novo padrão alfanumérico 2026)"
                        >
                          <Input placeholder="Ex: 00.000.000/0001-00 ou 14 dígitos" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="website"
                          label="Website Institucional"
                        >
                          <Input
                            prefix={<GlobalOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="https://suaempresa.com.br"
                            size="large"
                          />
                        </Form.Item>

                        {/* Informação somente leitura do vínculo com Grupo Corporativo */}
                        {org?.organization_groups ? (
                          <div style={{ padding: '14px 16px', background: 'rgba(114, 46, 209, 0.04)', borderRadius: 8, border: '1px solid rgba(114, 46, 209, 0.18)', marginBottom: 20 }}>
                            <Space align="start">
                              <ClusterOutlined style={{ color: '#722ed1', fontSize: 16, marginTop: 2 }} />
                              <div>
                                <Text strong style={{ color: '#722ed1', display: 'block', fontSize: 13 }}>
                                  Grupo Corporativo: {org.organization_groups.name}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  O vínculo a este grupo corporativo é estrutural e gerenciado por Super Administradores.
                                </Text>
                              </div>
                            </Space>
                          </div>
                        ) : (
                          <div style={{ padding: '14px 16px', background: 'rgba(0, 0, 0, 0.02)', borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.06)', marginBottom: 20 }}>
                            <Space align="start">
                              <ClusterOutlined style={{ color: '#8c8c8c', fontSize: 16, marginTop: 2 }} />
                              <div>
                                <Text strong style={{ display: 'block', fontSize: 13 }}>
                                  Tenant Independente
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Para vincular esta organização a um grupo corporativo, contate um Super Administrador.
                                </Text>
                              </div>
                            </Space>
                          </div>
                        )}
                      </Col>

                      {/* Integração ERP e Logo */}
                      <Col xs={24} md={12}>
                        <div className="org-section-header">
                          <BranchesOutlined style={{ color: '#1677ff' }} />
                          <span>Integração ERP SSW & Mídia</span>
                        </div>

                        <Form.Item
                          name="dominio"
                          label="Domínio no ERP SSW"
                          tooltip="Código de 3 ou mais letras atribuído à sua empresa no SSW (ex: DLG, CLD)."
                        >
                          <Input placeholder="Ex: DLG" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="sswBaseUrl"
                          label="URL de Login SSW"
                          tooltip="URL usada para autenticar a sessão do funcionário digital no ERP SSW (ex.: https://sistema.ssw.inf.br/bin/ssw0422). Deixe em branco para usar o padrão do ambiente."
                        >
                          <Input
                            placeholder="https://sistema.ssw.inf.br/bin/ssw0422"
                            size="large"
                          />
                        </Form.Item>

                        <Form.Item
                          name="logoUrl"
                          label="URL da Logomarca"
                          tooltip="Link direto da imagem do logotipo da sua empresa (PNG, SVG ou JPEG)."
                        >
                          <Input
                            prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="https://.../logo.png"
                            size="large"
                            onChange={(e) => setLogoPreview(e.target.value)}
                          />
                        </Form.Item>

                        {/* Pré-visualização do Logo */}
                        <div className="org-logo-preview-box">
                          <Avatar
                            size={48}
                            shape="square"
                            src={logoPreview}
                            icon={<BankOutlined />}
                            style={{ backgroundColor: '#1677ff', borderRadius: 8 }}
                          />
                          <div>
                            <Text strong style={{ display: 'block', fontSize: 13 }}>
                              Prévia da Identidade Visual
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {logoPreview ? 'Logotipo carregado a partir da URL informada' : 'Nenhuma imagem configurada (será exibido o ícone padrão)'}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <Divider style={{ margin: '24px 0' }} />

                    <Form.Item style={{ marginBottom: 0 }}>
                      <Space size="middle">
                        <Button
                          type="primary"
                          htmlType="submit"
                          icon={<SaveOutlined />}
                          loading={saving}
                          size="large"
                        >
                          Salvar Alterações
                        </Button>
                        <Button
                          onClick={() => {
                            load();
                            setActiveTab('overview');
                          }}
                          size="large"
                        >
                          Cancelar
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default OrganizationSettings;